import type { AdminRole } from "@repo/api-dtos"
import type {
	GameAdminOrpcClient,
	MarketplaceAdminOrpcClient,
} from "@/integrations/orpc/client"
import type { QueryClient } from "@tanstack/react-query"
import type { UseNavigateResult } from "@tanstack/react-router"

export type PaletteGroup =
	| "page"
	| "recent"
	| "results"
	| "navigate"
	| "create"
	| "run"
	| "switch"

export const PALETTE_GROUP_LABELS: Record<PaletteGroup, string> = {
	page: "On this page",
	recent: "Recent",
	results: "Results",
	navigate: "Navigate",
	create: "Create",
	run: "Run",
	switch: "Switch & account",
}

export interface PaletteStep {
	title: string
	placeholder?: string
	commands: PaletteCommand[]
}

export interface PaletteContext {
	navigate: UseNavigateResult<string>
	queryClient: QueryClient
	roles: AdminRole[]
	hasAnyRole: (roles: AdminRole[]) => boolean
	isSuperadmin: boolean
	marketplaceClient: MarketplaceAdminOrpcClient
	gameAdminClient: GameAdminOrpcClient
	close: () => void
	push: (step: PaletteStep) => void
	pushAsync: (title: string, load: () => Promise<PaletteCommand[]>) => void
}

export type PaletteIcon = React.ComponentType<{ className?: string }>

export interface PaletteCommand {
	id: string
	label: string
	description?: string
	keywords?: string[]
	icon?: PaletteIcon
	group: PaletteGroup
	shortcutHint?: string
	requiredRoles?: AdminRole[]
	/** Opened by ⌘Enter / ArrowRight; also shown as a `→` affordance. */
	submenu?: (ctx: PaletteContext) => PaletteStep | Promise<PaletteStep>
	perform: (ctx: PaletteContext) => void | Promise<void>
}

export function confirmStep(
	label: string,
	run: (ctx: PaletteContext) => void | Promise<void>,
): PaletteStep {
	return {
		title: label,
		placeholder: "Confirm?",
		commands: [
			{
				id: `confirm:${label}`,
				label: `Confirm: ${label}`,
				group: "run",
				perform: async (ctx) => {
					await run(ctx)
					ctx.close()
				},
			},
		],
	}
}

export function filterByRoles(
	commands: PaletteCommand[],
	hasAnyRole: (roles: AdminRole[]) => boolean,
): PaletteCommand[] {
	return commands.filter(
		(command) => !command.requiredRoles || hasAnyRole(command.requiredRoles),
	)
}
